import { fonts, mappedColors } from '@pixee/design-system'
import { RAW_COLORS } from '@pixee/design-system/primitives'
import { useEffect, useRef } from 'react'
import { Spinner } from '../../components/spinner.tsx'
import { pluralize } from '../../utils/text-utils.ts'

// `mappedColors.primary` is a curated semantic subset and does not expose `pulse`, so reach for the
// raw token for the scanner's bright purple (#C935FF).
const PURPLE = RAW_COLORS.primary.pulse

type Find = { angle: number; radiusFraction: number }

const MAX_FINDS = 60
// The golden angle (~137.5°) spreads points evenly around the circle with consecutive indices far
// apart — used only under reduced motion, where there is no sweep line to anchor finds to.
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5))
// Same low-discrepancy trick on the radius, so findings discovered a frame apart (and therefore at
// nearly the same sweep angle) land at clearly different distances instead of stamping on top of
// each other.
const GOLDEN_RATIO_CONJUGATE = 2 / (1 + Math.sqrt(5))
// Keep the central count text clear by distributing logos in an annulus (inner..1.0 of the radius),
// area-uniform so they fill it evenly instead of crowding the middle.
const INNER_FRACTION = 0.58
const radiusFractionFor = (index: number): number => {
  const spread = (index * GOLDEN_RATIO_CONJUGATE) % 1
  return Math.sqrt(INNER_FRACTION * INNER_FRACTION + spread * (1 - INNER_FRACTION * INNER_FRACTION))
}

/**
 * A radar-style scanning animation with a live count of findings discovered so far. The count is the
 * durable, meaningful signal (driven by the server's generation status); the sweep and lit dots are
 * ambient. Motion is disabled under `prefers-reduced-motion`, where the canvas simply repaints when
 * the count changes.
 */
export const AiScanProgressCanvas: React.FC<{ findingsCount: number; label?: string }> = ({ findingsCount, label }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  // Read live inside the animation loop without restarting it on every count change.
  const findingsRef = useRef(findingsCount)
  findingsRef.current = findingsCount
  const drawRef = useRef<() => void>(() => {})

  useEffect(() => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) {
      return
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const finds: Find[] = []
    let sweep = -Math.PI / 2

    // A finding is stamped wherever the sweep line is at the moment it is discovered, the way a radar
    // contact only ever appears under the beam — a logo popping into empty space away from the line
    // reads as a glitch. Under reduced motion there is no beam, so spread them around the circle.
    const syncFinds = () => {
      const target = Math.min(findingsRef.current, MAX_FINDS)
      // A restarted scan resets the count; drop the stale contacts.
      finds.length = Math.min(finds.length, target)
      while (finds.length < target) {
        const index = finds.length
        finds.push({
          angle: prefersReducedMotion ? -Math.PI / 2 + index * GOLDEN_ANGLE : sweep,
          radiusFraction: radiusFractionFor(index),
        })
      }
    }

    // Each discovered finding is marked with the Pixee logo rather than a plain dot. The image loads
    // async, so repaint once it is ready (the animation loop already repaints; this covers reduced
    // motion, where there is no loop).
    const logo = new Image()
    let isLogoReady = false
    logo.onload = () => {
      isLogoReady = true
      drawRef.current()
    }
    logo.src = '/0-Icon-Trimmed.png'

    const draw = () => {
      syncFinds()

      const dpr = window.devicePixelRatio || 1
      const { width, height } = canvas.getBoundingClientRect()
      if (canvas.width !== Math.round(width * dpr) || canvas.height !== Math.round(height * dpr)) {
        canvas.width = Math.round(width * dpr)
        canvas.height = Math.round(height * dpr)
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.clearRect(0, 0, width, height)

      const centerX = width / 2
      const centerY = height / 2
      const maxRadius = Math.min(width, height) / 2 - 10

      // Bright purple rings: a soft glow pass behind, then a crisp opaque core on top so the line
      // reads as vivid `pulse` rather than getting washed out by the blur.
      ctx.strokeStyle = PURPLE
      const ringFractions = [0.4, 0.68, 0.94]
      ctx.globalAlpha = 0.3
      ctx.lineWidth = 5
      ctx.shadowColor = PURPLE
      ctx.shadowBlur = 12
      for (const fraction of ringFractions) {
        ctx.beginPath()
        ctx.arc(centerX, centerY, maxRadius * fraction, 0, Math.PI * 2)
        ctx.stroke()
      }
      ctx.shadowBlur = 0
      ctx.globalAlpha = 1
      ctx.lineWidth = 1.75
      for (const fraction of ringFractions) {
        ctx.beginPath()
        ctx.arc(centerX, centerY, maxRadius * fraction, 0, Math.PI * 2)
        ctx.stroke()
      }

      const logoSize = 15
      for (const find of finds) {
        const x = centerX + Math.cos(find.angle) * maxRadius * find.radiusFraction
        const y = centerY + Math.sin(find.angle) * maxRadius * find.radiusFraction
        if (isLogoReady) {
          ctx.drawImage(logo, x - logoSize / 2, y - logoSize / 2, logoSize, logoSize)
          continue
        }
        // Logo still loading: a purple dot holds the contact's spot.
        ctx.fillStyle = PURPLE
        ctx.beginPath()
        ctx.arc(x, y, 3, 0, Math.PI * 2)
        ctx.fill()
      }

      if (!prefersReducedMotion) {
        ctx.fillStyle = PURPLE
        ctx.globalAlpha = 0.16
        ctx.beginPath()
        ctx.moveTo(centerX, centerY)
        ctx.arc(centerX, centerY, maxRadius, sweep - 0.5, sweep)
        ctx.closePath()
        ctx.fill()

        ctx.strokeStyle = PURPLE
        ctx.globalAlpha = 0.9
        ctx.lineWidth = 1.5
        ctx.beginPath()
        ctx.moveTo(centerX, centerY)
        ctx.lineTo(centerX + Math.cos(sweep) * maxRadius, centerY + Math.sin(sweep) * maxRadius)
        ctx.stroke()
        ctx.globalAlpha = 1
      }
    }
    drawRef.current = draw

    if (prefersReducedMotion) {
      draw()
      return
    }

    let animationFrame = 0
    const loop = () => {
      sweep += 0.03
      draw()
      animationFrame = requestAnimationFrame(loop)
    }
    loop()
    return () => cancelAnimationFrame(animationFrame)
  }, [])

  // Under reduced motion there is no loop, so repaint when the count changes.
  useEffect(() => {
    drawRef.current()
  }, [findingsCount])

  return (
    <div style={{ position: 'relative', width: '100%', height: '260px' }}>
      <canvas ref={canvasRef} aria-hidden style={{ width: '100%', height: '100%', display: 'block' }} />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '2px',
          pointerEvents: 'none',
          textAlign: 'center',
        }}
        role="status"
        aria-live="polite"
      >
        <span style={{ fontSize: '44px', fontWeight: 700, lineHeight: 1, color: mappedColors.neutral.halo }}>
          {findingsCount}
        </span>
        <span style={{ font: fonts.small, color: mappedColors.neutral.neutral20 }}>
          {pluralize(findingsCount, 'finding', 'findings')} found
        </span>
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginTop: '8px',
            font: fonts.extraSmall,
            color: mappedColors.neutral.neutral40,
          }}
        >
          <Spinner size="16" label="Scanning" />
          {label ?? 'Scanning repository…'}
        </span>
      </div>
    </div>
  )
}
